import { useId, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { interpretarDataHora, paraCampoDataHora } from '@/lib/formato';
import { opcaoDaPessoa } from '@/modulos/equipamentos/opcoesComVisual';
import { useSessao } from '@/modulos/sessao/consultas';

import type { ParadaProgramada } from './api';
import { combinadaSugerida } from './autopreenchimento';
import { explicarFalha, useCadastrosDaOficina, useOrdens, useProgramarParada } from './consultas';
import {
  momentoDaParada,
  problemasDaParada,
  ROTULO_DO_MOMENTO,
  textoDaJanela,
  textoDeQuemCombinou,
  type CampoDaParada,
} from './parada';

/**
 * A PARADA PROGRAMADA na mesa (0089, módulo 05, ferramenta 4): a janela em
 * que a máquina sai da operação, combinada com quem responde pela frente.
 *
 * Duas peças, para as duas telas que a usam: o RESUMO (uma linha que diz a
 * janela e quem combinou) e o FORMULÁRIO (saída, volta e com quem). O painel
 * da O.S. mostra os dois em linha; a manutenção abre o formulário em janela.
 */

/** "08/10/2026 · 07:00 às 17:00 · combinada com ANA por CARLOS", ou o que falta. */
export function ResumoDaParada({ parada }: { readonly parada: ParadaProgramada | null | undefined }) {
  if (!parada) {
    return <span className="txt-apoio text-tinta-apagada">Sem data combinada com a operação.</span>;
  }
  const de = new Date(parada.de);
  const ate = new Date(parada.ate);
  const momento = momentoDaParada(de, ate);
  const quem = textoDeQuemCombinou(parada.combinadaCom, parada.combinadaPor);
  return (
    <span className="flex flex-col">
      <span className="txt-corpo text-tinta">{textoDaJanela(de, ate)}</span>
      <span className={`txt-apoio ${momento === 'atrasada' ? 'text-parada' : 'text-tinta-secundaria'}`}>
        {[ROTULO_DO_MOMENTO[momento], quem].filter(Boolean).join(' · ')}
      </span>
    </span>
  );
}

/**
 * Programar (ou reprogramar) a parada. Só a pessoa da OPERAÇÃO se escolhe: o
 * lado da manutenção é quem está gravando, e o servidor o tira da sessão.
 */
export function FormularioDaParada({
  ordemId,
  atual,
  aoGravar,
  aoCancelar,
}: {
  readonly ordemId: string;
  readonly atual: ParadaProgramada | null | undefined;
  readonly aoGravar?: () => void;
  /** Com ele, o formulário ganha o botão de sair (na janela). */
  readonly aoCancelar?: () => void;
}) {
  const base = useId();
  const sessao = useSessao();
  const eu = sessao.data?.usuarioId ?? null;
  const cadastros = useCadastrosDaOficina();
  const ordens = useOrdens();
  const programar = useProgramarParada();

  const [de, setDe] = useState(atual ? paraCampoDataHora(new Date(atual.de)) : '');
  const [ate, setAte] = useState(atual ? paraCampoDataHora(new Date(atual.ate)) : '');
  /* `null` = a pessoa ainda não escolheu: vale a da programação atual, ou a
     com quem a última parada desta máquina foi combinada (31/10/2026). */
  const [comEscolhida, setCom] = useState<string | null>(atual?.combinadaComId ?? null);
  const daOrdem = (ordens.data ?? []).find((o) => o.id === ordemId);
  const sugerida = daOrdem ? combinadaSugerida(ordens.data ?? [], daOrdem.equipamentoId, eu) : null;
  const com = comEscolhida ?? sugerida?.id ?? '';
  const comDoSistema = comEscolhida === null && com !== '';
  const [erros, setErros] = useState<Partial<Record<CampoDaParada, string | null>>>({});
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);

  const rascunho = { de, ate, combinadaComId: com };
  /* Valida AO SAIR do campo (p.16) e limpa enquanto a pessoa corrige. */
  const sair = (campo: CampoDaParada) => {
    setErros((e) => ({ ...e, [campo]: problemasDaParada(rascunho, eu)[campo] ?? null }));
  };
  const limpar = (campo: CampoDaParada) => {
    setErros((e) => ({ ...e, [campo]: null }));
  };
  const marca = (campo: CampoDaParada) =>
    erros[campo] ? { 'aria-invalid': true as const, 'aria-describedby': `${base}-${campo}-erro` } : {};

  /* A pessoa da operação, e nunca quem grava: a lista já não a oferece. */
  const pessoas = (cadastros.data?.pessoas ?? []).filter((p) => p.id !== eu);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = problemasDaParada(rascunho, eu);
    setErros(achados);
    if (Object.values(achados).some(Boolean)) return;
    const saida = interpretarDataHora(de);
    const volta = interpretarDataHora(ate);
    if (!saida || !volta) return;
    setRecusa(null);
    programar.mutate(
      { id: ordemId, envio: { de: saida.toISOString(), ate: volta.toISOString(), combinadaComId: com } },
      {
        onSuccess: () => aoGravar?.(),
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
      <div className="formulario-grade">
        <Campo id={`${base}-de`} rotulo="Início" ajuda="Quando a máquina sai da operação." erro={erros.de ?? null}>
          <input
            id={`${base}-de`}
            type="datetime-local"
            className="controle w-full"
            value={de}
            onChange={(e) => {
              setDe(e.target.value);
              limpar('de');
            }}
            onBlur={() => {
              sair('de');
            }}
            {...marca('de')}
          />
        </Campo>
        <Campo id={`${base}-ate`} rotulo="Fim" ajuda="Quando ela volta para a frente." erro={erros.ate ?? null}>
          <input
            id={`${base}-ate`}
            type="datetime-local"
            className="controle w-full"
            value={ate}
            onChange={(e) => {
              setAte(e.target.value);
              limpar('ate');
            }}
            onBlur={() => {
              sair('ate');
            }}
            {...marca('ate')}
          />
        </Campo>
        <div className="min-w-0">
          <Campo
            id={`${base}-combinadaComId`}
            rotulo="Combinada com, pela operação"
            erro={erros.combinadaComId ?? null}
            preenchido={comDoSistema}
            ajuda={
              comDoSistema && !atual
                ? `Com quem a última parada desta máquina foi combinada${daOrdem ? `, ${daOrdem.equipamento}` : ''}. Troque se for outra pessoa.`
                : 'Quem aceitou tirar a máquina da frente. Pela manutenção, fica registrado o seu nome.'
            }
          >
            {cadastros.isPending ? (
              <ControleCarregando oQue="Carregando as pessoas" />
            ) : (
              /* O seletor do sistema, com busca e as iniciais (31/10/2026). */
              <ListaDeEscolha
                id={`${base}-combinadaComId`}
                rotulo="Combinada com, pela operação"
                valor={com}
                aoMudar={(v) => {
                  setCom(v);
                  limpar('combinadaComId');
                }}
                aoSair={() => {
                  sair('combinadaComId');
                }}
                rotuloVazio="Escolha a pessoa"
                opcoes={pessoas.map((p) => opcaoDaPessoa(p))}
                idDoErro={erros.combinadaComId ? `${base}-combinadaComId-erro` : undefined}
              />
            )}
          </Campo>
        </div>
      </div>

      {recusa === null ? null : (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{recusa.erro}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      )}

      {aoCancelar ? (
        /* Na janela: o primário à direita, o de sair à esquerda dele (p.29). */
        <div className="barra-de-acoes">
          <Botao nivel="terciario" type="button" onClick={aoCancelar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" type="submit" disabled={programar.isPending}>
            {atual ? 'Reprogramar parada' : 'Programar parada'}
          </Botao>
        </div>
      ) : (
        /* No painel da O.S. o laranja é de outra ação: aqui, secundário, à
           direita, "Salvar parada" (R06, p.70). */
        <div className="flex justify-end">
          <Botao nivel="secundario" type="submit" disabled={programar.isPending}>
            Salvar parada
          </Botao>
        </div>
      )}
    </form>
  );
}
